/**
 * Update card mutation with optimistic locking via revisions field.
 */
export const mutationUpdateCardWithVersion = `mutation updateCard($input: UpdateCardInput!) {
  updateCard(input: $input) {
    cardId
    title
    revisions
    dateUpdated
  }
}`;

/**
 * Input type for update card mutation.
 */
export interface UpdateCardInput {
	cardId: string;
	patch: CardItemPatchInput;
	/** Current revision for optimistic locking */
	revisions: number;
}

/**
 * Patch input for card updates.
 * Supports `null` for clearing string fields.
 */
export interface CardItemPatchInput {
	title?: string | null;
	subtitle?: string | null;
	/** Markdown / HTML body and other text payloads on non-chart cards */
	content?: string | null;
	clientId?: string | null;
	pageId?: string | null;
	cardType?: string | null;
	chartType?: string | null;
	paletteId?: string | null;
	data?: CardDataInput;
	/** AppSync AWSJSON — send a JSON string, not a nested GraphQL object. */
	visualization?: string;
	/** AppSync AWSJSON — send a JSON string, not a nested GraphQL object. */
	presentation?: string;
	style?: CardStyleInput;
	icon?: string | null;
	image?: string | null;
	/** Saved library filters linked on this card (`CardInput.filterIds`). */
	filterIds?: string[];
}

/**
 * Card data input structure.
 */
export interface CardDataInput {
	metrics?: MetricInput[];
	dimensions?: DimensionInput[];
	timeDimension?: TimeDimensionInput;
	filters?: FilterInput[] | string;
	/**
	 * Dropdown filter default selection — awaits `selected: [String]` on AppSync
	 * `CardDataConfigInput` (gated by `DROPDOWN_FILTER_GQL_PERSIST_VIA_SELECTED`
	 * in `card-data-gql.util.ts`).
	 */
	selected?: string[];
	/** AppSync `CardDataConfigInput.order` is `AWSJSON` — JSON string of `{ member: asc|desc }`. */
	order?: string;
}

/**
 * Metric input structure.
 */
export interface MetricInput {
	id: string;
	name: string;
	label?: string;
	dataType?: string;
	aggregation?: string;
}

/**
 * Dimension input structure.
 */
export interface DimensionInput {
	id: string;
	name: string;
	label?: string;
	dataType?: string;
}

/**
 * Time dimension input structure.
 */
export interface TimeDimensionInput {
	id: string;
	name: string;
	label?: string;
	dataType?: string;
}

/**
 * Filter input structure.
 */
export interface FilterInput {
	field: string;
	operator: string;
	value: unknown;
}

/**
 * Card style input structure.
 */
export interface CardStyleInput {
	card?: CardStyleCardInput;
	colors?: CardStyleColorsInput;
}

/**
 * Card style card input.
 */
export interface CardStyleCardInput {
	padding?: number;
	contentAlignment?: string;
}

/**
 * Card style colors input.
 */
export interface CardStyleColorsInput {
	light?: CardStyleColorModeInput;
	dark?: CardStyleColorModeInput;
}

/**
 * Card style color mode input.
 */
export interface CardStyleColorModeInput {
	card?: CardStyleColorCardInput;
}

/**
 * Card style color card input.
 */
export interface CardStyleColorCardInput {
	background?: string;
	border?: string;
	shadow?: string;
}

/**
 * Response from update card mutation.
 */
export interface UpdateCardResponse {
	cardId: string;
	title: string;
	revisions: number;
	dateUpdated: string;
}
